import { useState } from 'react';
import { Link } from 'react-router-dom';
import QuickBooksSyncDialog from './QuickBooksSyncDialog.jsx';
import {
  INTEGRATION_CATALOG,
  INTEGRATION_PRICING,
  formatIntegrationPrice,
  integrationPricingForConnections,
} from '../../lib/settingsStore.js';
import { formatMatterMoney } from '../../lib/matterCurrency.js';

const INTEGRATION_MARKS = {
  QUICKBOOKS: 'QB',
  ZOHO_BOOKS: 'ZB',
  XERO: 'Xe',
  SAGE: 'Sg',
  NETSUITE: 'NS',
  FRESHBOOKS: 'FB',
  WAVE: 'Wv',
};

function integrationMark(id) {
  return INTEGRATION_MARKS[id] || String(id || '').slice(0, 2);
}

function formatSyncWhen(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) return 'today';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function featureSubtitle(conn) {
  const parts = [];
  if (conn.primary) parts.push('Primary connection');
  if (conn.realmId) parts.push(`Company ${conn.realmId}`);
  if (!parts.length && conn.connectedAt) {
    parts.push(`Connected ${formatSyncWhen(conn.connectedAt)}`);
  }
  return parts.join(' · ') || conn.description;
}

const REQUEST_INTEGRATION_MAILTO =
  'mailto:support@demandjetapp.com?subject=' +
  encodeURIComponent('Request a new DemandJet accounting integration') +
  '&body=' +
  encodeURIComponent(
    [
      'Hello DemandJet support,',
      '',
      'I would like to request a new accounting software integration.',
      '',
      'Accounting software name:',
      'Company / organization:',
      'Why we need this integration:',
      '',
      'Thank you.',
    ].join('\n')
  );

export default function IntegrationsPanel({
  connections,
  connectProvider,
  connectMessage,
  onOpenConnect,
  onContinueOAuth,
  onDemoConnect,
  onCancelConnect,
  onDisconnect,
  onSyncQuickBooks,
  syncResult,
  syncNotice,
  syncing = false,
  syncProgress,
  syncChoice,
  autoSyncText,
  pending,
}) {
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const providerMeta = connectProvider
    ? INTEGRATION_CATALOG.find((p) => p.id === connectProvider)
    : null;
  const pricing = integrationPricingForConnections(connections);
  const unitPrice = formatIntegrationPrice(INTEGRATION_PRICING.connectionMonthly);
  const connectPrice = providerMeta ? pricing.byId[providerMeta.id] : null;
  const connectedApps = connections.filter((conn) => conn.status === 'connected');
  const availableApps = connections.filter((conn) => conn.status !== 'connected');

  return (
    <div className="settings-form">
      <h2>Integrations</h2>
      <p className="settings-lead integration-lead">
        Each connection is {unitPrice}/mo. Demands and archive are not metered.
      </p>

      {connectedApps.length ? (
        <div className="integration-connected">
          {connectedApps.map((conn) => {
            const isQuickBooks = conn.id === 'QUICKBOOKS';
            const price = pricing.byId[conn.id];
            return (
              <article key={conn.id} className="integration-feature">
                <div className="integration-feature-main">
                  <div
                    className={`integration-mark${isQuickBooks ? ' is-quickbooks' : ''}`}
                    aria-hidden="true"
                  >
                    {integrationMark(conn.id)}
                  </div>
                  <div className="integration-feature-copy">
                    <h3>
                      {conn.name}
                      {conn.demo ? <span className="demo-pill">Demo</span> : null}
                    </h3>
                    <p>{featureSubtitle(conn)}</p>
                    <div className="integration-feature-meta">
                      <span className="integration-chip">
                        <i aria-hidden="true" />
                        Connected
                      </span>
                      {conn.lastSyncAt ? (
                        <span>Last sync {formatSyncWhen(conn.lastSyncAt)}</span>
                      ) : null}
                      {price ? <span>{unitPrice}/mo</span> : null}
                    </div>
                    {isQuickBooks && !conn.demo && autoSyncText ? (
                      <p className="integration-feature-note">{autoSyncText}</p>
                    ) : null}
                  </div>
                  <div className="integration-feature-actions">
                    {isQuickBooks ? (
                      <button
                        type="button"
                        className={`btn-primary integration-pill${syncing ? ' is-busy' : ''}`}
                        onClick={() => setSyncDialogOpen(true)}
                        disabled={pending || syncing}
                        aria-busy={syncing}
                      >
                        {syncing ? (
                          <>
                            <span className="btn-spinner" aria-hidden="true" />
                            Syncing…
                          </>
                        ) : (
                          'Sync invoices'
                        )}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn-ghost integration-pill"
                      onClick={() => onDisconnect(conn.id)}
                      disabled={pending || syncing}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
                {isQuickBooks && syncing ? (
                  <div className="integration-feature-status sync-progress" role="status" aria-live="polite">
                    <div className="sync-progress-text">
                      {syncProgress?.total
                        ? `Importing invoices… ${syncProgress.processed} of ${syncProgress.total} checked · ${syncProgress.imported} imported`
                        : 'Checking QuickBooks for invoices… this can take a few seconds.'}
                    </div>
                    <div
                      className={`sync-progress-bar${syncProgress?.total ? '' : ' is-indeterminate'}`}
                    >
                      <span
                        style={
                          syncProgress?.total
                            ? {
                                width: `${Math.round(
                                  (syncProgress.processed / syncProgress.total) * 100
                                )}%`,
                              }
                            : undefined
                        }
                      />
                    </div>
                    <small className="field-hint">
                      You can leave this page. The sync keeps running in the background.
                    </small>
                  </div>
                ) : null}
                {isQuickBooks && syncNotice ? (
                  <p
                    className={`integration-feature-status sync-notice ${
                      syncNotice.tone === 'error' ? 'is-error' : 'is-success'
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    {syncNotice.text}
                    {syncNotice.tone === 'success' && syncNotice.imported > 0 ? (
                      <>
                        {' '}
                        <Link className="text-link strong" to="/client/demands">
                          View demands →
                        </Link>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      {availableApps.length ? (
        <>
          <h3 className="integration-catalog-label">Add an app</h3>
          <div className="integration-catalog">
            {availableApps.map((conn) => (
                <article key={conn.id} className="integration-app">
                  <div className="integration-mark" aria-hidden="true">
                    {integrationMark(conn.id)}
                  </div>
                  <div>
                    <h3>{conn.name}</h3>
                    <p>{unitPrice}/mo</p>
                  </div>
                  <button
                    type="button"
                    className="btn-ghost integration-pill"
                    onClick={() => onOpenConnect(conn.id)}
                    disabled={pending}
                  >
                    Connect
                  </button>
                </article>
            ))}
          </div>
        </>
      ) : null}

      <article className="integration-request">
        <div>
          <h3>Request a new integration</h3>
          <p>Don’t see your accounting software? Tell us which platform you need.</p>
        </div>
        <a className="btn-ghost integration-pill" href={REQUEST_INTEGRATION_MAILTO}>
          Request integration
        </a>
      </article>

      {syncResult ? (
        <div className="connect-box">
          <h3>Last QuickBooks sync</h3>
          <p>
            Imported <strong>{syncResult.imported}</strong> open invoice
            {syncResult.imported === 1 ? '' : 's'}
            {syncResult.skipped ? ` · skipped ${syncResult.skipped}` : ''}
            {syncResult.mock ? ' · demo data' : ''}.
          </p>
          {syncResult.matters?.length ? (
            <ul className="sync-matter-list">
              {syncResult.matters.map((m) => (
                <li key={m.ROWID}>
                  <strong>{m.Debtor_Name}</strong>
                  <span>
                    {m.Debtor_State} · {formatMatterMoney(m.Amount_Owed, m)} ·{' '}
                    {m.External_Invoice_ID}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <p style={{ marginTop: '0.75rem' }}>
            <Link className="text-link strong" to="/client/demands">
              Open workspace to send demands from QuickBooks →
            </Link>
          </p>
        </div>
      ) : null}

      {connections.some((c) => c.id === 'QUICKBOOKS' && c.status === 'connected') ? (
        <div className="connect-box">
          <h3>Send demands from QuickBooks</h3>
          <p>
            Choose open invoices in your company file and send them into the DemandJet attorney
            pipeline as automated demands.
          </p>
          <Link className="btn-primary conn-connect" to="/client/demands">
            Go to Send from QuickBooks
          </Link>
        </div>
      ) : null}

      {providerMeta ? (
        <div className="social-modal-backdrop" role="presentation" onClick={onCancelConnect}>
          <div
            className="social-modal connect-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="connect-provider-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="social-modal-head">
              <h2 id="connect-provider-title">Connect {providerMeta.name}</h2>
              <button
                type="button"
                className="social-modal-close"
                onClick={onCancelConnect}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="social-modal-hint">
              You’ll be sent to {providerMeta.name} to sign in and grant DemandJet permission to
              import invoices and update payment status.
            </p>
            {connectPrice ? (
              <p className="integration-connect-price">
                This connection bills at <strong>{connectPrice.label}</strong> as an integration
                add-on.
              </p>
            ) : null}
            {connectMessage ? <p className="settings-sublead">{connectMessage}</p> : null}
            <div className="connect-modal-actions">
              <button
                type="button"
                className="btn-primary social-modal-create"
                onClick={onContinueOAuth}
                disabled={pending}
              >
                {pending ? 'Opening…' : `Continue to ${providerMeta.name}`}
              </button>
              <button
                type="button"
                className="btn-ghost"
                onClick={onDemoConnect}
                disabled={pending}
              >
                Use demo connection
              </button>
              <button type="button" className="btn-ghost" onClick={onCancelConnect}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {syncDialogOpen ? (
        <QuickBooksSyncDialog
          initialChoice={syncChoice}
          onCancel={() => setSyncDialogOpen(false)}
          onSync={(choice) => {
            setSyncDialogOpen(false);
            onSyncQuickBooks(choice);
          }}
        />
      ) : null}
    </div>
  );
}
